import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import toast from "react-hot-toast";
import { showRefinery, getErpBranchDetails, getTenantBrandImageSrcWithFallback } from "../erpClient";
import {
  isRefineryApiSuccess,
  mapRefineryShowToForm,
} from "../utils/refineryErpAdapter";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getTenantNameUpper } from "../utils/tenantName";

const BORDER_COLOR = "#8c1d1a";
const BORDER = `1pt solid ${BORDER_COLOR}`;
const HEADER_BG = "#f5e9e9";
const BORDER_RADIUS = 6;
const TABLE_BLANK_HEIGHT = 50;
const FONT_SIZE = 9;

// A5 landscape — 8mm gap between page edge and content (all sides), same as Repairing
const PAGE_MARGIN = 22.68; // 8mm in pt (8 * 72 / 25.4)
const A5_LANDSCAPE = [595.28, 419.53]; // [width, height]

const styles = StyleSheet.create({
  page: {
    padding: PAGE_MARGIN,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
    fontSize: FONT_SIZE,
    color: "#000",
  },
  pageContent: {
    flex: 1,
    flexDirection: "column",
  },
  mainBox: {
    border: BORDER,
    borderRadius: BORDER_RADIUS,
    flexDirection: "column",
  },
  headerBlock: {
    position: "relative",
    paddingTop: 6,
    paddingBottom: 5,
    paddingHorizontal: 8,
    minHeight: 44,
  },
  headerLogoCol: {
    position: "absolute",
    left: 8,
    top: 6,
    width: 36,
    height: 36,
  },
  headerLogo: {
    height: 32,
    width: 32,
  },
  headerMobileCol: {
    position: "absolute",
    right: 8,
    top: 12,
  },
  headerMobileText: {
    fontSize: 9.5,
    fontWeight: "bold",
    textAlign: "right",
  },
  headerCenterBrand: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 50,
  },
  companyTitle: {
    fontFamily: "Times-Bold",
    fontSize: 26,
    color: "#7d0000",
    letterSpacing: 0.4,
    textAlign: "center",
    lineHeight: 1,
  },
  companyAddress: {
    fontSize: 7,
    marginTop: 2,
    textAlign: "center",
    width: "100%",
  },
  sectionDivider: {
    borderTop: BORDER,
  },
  infoBlock: {
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 88,
  },
  infoLeft: {
    width: "62%",
    borderRight: BORDER,
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  customerFields: {
    flex: 1,
    justifyContent: "space-between",
    height: 76,
    paddingVertical: 2,
  },
  infoRight: {
    width: "38%",
    paddingVertical: 5,
    paddingHorizontal: 6,
    justifyContent: "flex-start",
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  infoRowSplit: {
    flexDirection: "row",
  },
  infoHalf: {
    flexDirection: "row",
    width: "50%",
    paddingRight: 3,
  },
  infoLabel: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    width: 46,
  },
  infoColon: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    width: 5.5,
  },
  infoValue: {
    flex: 1,
    fontSize: FONT_SIZE,
  },
  // Outer frame only — no overflow:hidden (clips border curves in react-pdf)
  voucherBox: {
    border: BORDER,
    borderRadius: 4,
    marginBottom: 4,
  },
  // Match top curve of voucherBox so pink fill follows the border
  voucherBoxHeader: {
    backgroundColor: HEADER_BG,
    borderBottom: BORDER,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingVertical: 3,
    alignItems: "center",
  },
  voucherBoxHeaderText: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  voucherBoxBody: {
    paddingVertical: 3,
    paddingHorizontal: 5,
    fontSize: FONT_SIZE,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  voucherBoxRow: {
    flexDirection: "row",
    marginBottom: 1,
    alignItems: "center",
  },
  voucherLabel: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    width: 56,
  },
  voucherColon: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    width: 5,
  },
  voucherValue: {
    fontSize: FONT_SIZE,
    flex: 1,
  },
  voucherMetaRow: {
    flexDirection: "row",
    marginBottom: 1,
    alignItems: "center",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: HEADER_BG,
    borderBottom: BORDER,
    fontWeight: "bold",
    fontSize: FONT_SIZE,
  },
  tableDataRow: {
    flexDirection: "row",
    minHeight: 22,
  },
  tableBlankArea: {
    flexDirection: "row",
    minHeight: TABLE_BLANK_HEIGHT,
  },
  blankCell: {
    borderRight: BORDER,
    minHeight: TABLE_BLANK_HEIGHT,
  },
  blankCellLast: {
    minHeight: TABLE_BLANK_HEIGHT,
  },
  // Pink total row sits on mainBox bottom — follow outer corner radius
  tableFooterRow: {
    flexDirection: "row",
    backgroundColor: HEADER_BG,
    fontWeight: "bold",
    fontSize: FONT_SIZE,
    borderTop: BORDER,
    borderBottomLeftRadius: BORDER_RADIUS,
    borderBottomRightRadius: BORDER_RADIUS,
  },
  cell: {
    padding: 4,
    paddingVertical: 5,
    borderRight: BORDER,
    fontSize: FONT_SIZE,
    justifyContent: "center",
  },
  cellLast: {
    padding: 4,
    paddingVertical: 5,
    fontSize: FONT_SIZE,
    justifyContent: "center",
  },
  colSr: { width: "4%", textAlign: "center" },
  colDesc: { width: "28%", textAlign: "center" },
  colHsn: { width: "10%", textAlign: "center" },
  colWt: { width: "8%", textAlign: "center" },
  colTouch: { width: "8%", textAlign: "center" },
  colFine: { width: "10%", textAlign: "center" },
  colConv: { width: "10%", textAlign: "center" },
  colTotal: { width: "14%", textAlign: "center" },
  remarksBox: {
    border: BORDER,
    borderRadius: BORDER_RADIUS,
    marginTop: 4,
    minHeight: 22,
    paddingVertical: 3,
    paddingHorizontal: 5,
    flexDirection: "row",
  },
  remarksLabel: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    marginRight: 4,
  },
  remarksText: {
    flex: 1,
    fontSize: FONT_SIZE,
  },
  signatureBox: {
    border: BORDER,
    borderRadius: BORDER_RADIUS,
    marginTop: 4,
    paddingVertical: 8,
    paddingHorizontal: 8,
    minHeight: 72,
  },
  signatureTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  signatureBottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  signatureLeft: {
    alignItems: "flex-start",
    width: "42%",
  },
  signatureRight: {
    alignItems: "flex-end",
    width: "42%",
  },
  jurisdictionText: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  forCompanyText: {
    fontSize: FONT_SIZE,
    fontWeight: "bold",
    textAlign: "right",
  },
  signatureLine: {
    borderBottom: BORDER,
    width: 145,
    marginBottom: 3,
    marginTop: 28,
  },
  signatureImage: {
    width: 80,
    height: 30,
    objectFit: "contain",
    marginBottom: 2,
  },
  signatureCaptionLeft: {
    fontSize: FONT_SIZE,
    textAlign: "left",
  },
  signatureCaptionRight: {
    fontSize: FONT_SIZE,
    textAlign: "right",
  },
});

const fmtWt = (v, digits = 3) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : "";
};

const fmtTouch = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

const formatDate = (dateString) => {
  if (!dateString) return new Date().toLocaleDateString("en-GB");
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    return date.toLocaleDateString("en-GB");
  } catch {
    return new Date().toLocaleDateString("en-GB");
  }
};

const InfoRow = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoColon}>:</Text>
    <Text style={styles.infoValue}>{value || ""}</Text>
  </View>
);

const LabelValue = ({ label, value, labelStyle, colonStyle, valueStyle }) => (
  <View style={styles.infoHalf}>
    <Text style={labelStyle || styles.infoLabel}>{label}</Text>
    <Text style={colonStyle || styles.infoColon}>:</Text>
    <Text style={valueStyle || styles.infoValue}>{value || ""}</Text>
  </View>
);

const RefineryPDFDocument = ({ docData }) => {
  const items = docData?.items || [];

  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyName = getTenantNameUpper();
  const companyMobile =
    branchDetails.mobile || branchDetails.phone || "-";
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() || "-";
  const companyGst =
    branchDetails.gst_number ||
    branchDetails.gstin ||
    branchDetails.GST_number ||
    "-";
  const companyPan =
    branchDetails.pan_card ||
    branchDetails.pan_no ||
    branchDetails.pan ||
    "-";

  const totals = items.reduce(
    (acc, item) => {
      acc.gwt += Number(item.gwt) || 0;
      acc.nwt += Number(item.nwt) || 0;
      acc.fineWt += Number(item.fine_wt) || 0;
      acc.totalWt += Number(item.total_wt) || 0;
      return acc;
    },
    { gwt: 0, nwt: 0, fineWt: 0, totalWt: 0 },
  );

  const supplierName = docData?.supplier_name || "";
  const supplierAddress = docData?.supplier_address || "";
  const supplierCity = docData?.supplier_city || "";
  const supplierPhone = docData?.supplier_mobile || "";
  const supplierState = docData?.supplier_state || "Gujarat";
  const supplierGst = docData?.supplier_gst_number || "";
  const supplierPan = docData?.supplier_pan_card || "";

  return (
    <Document>
      <Page size={A5_LANDSCAPE} style={styles.page} wrap={false}>
        <View style={styles.pageContent}>
          {/* Section 1: Header + info + table */}
          <View style={styles.mainBox}>
            <View style={styles.headerBlock}>
              <View style={styles.headerMobileCol}>
                <Text style={styles.headerMobileText} wrap={false}>
                  MO.: {companyMobile}
                </Text>
              </View>
              <View style={styles.headerLogoCol}>
                <Image
                  src={getTenantBrandImageSrcWithFallback("sidebar_icon")}
                  style={styles.headerLogo}
                />
              </View>
              <View style={styles.headerCenterBrand}>
                <Text style={styles.companyTitle} wrap={false}>
                  {companyName}
                </Text>
                <Text style={styles.companyAddress} wrap={false}>
                  {addressSingleLine}
                </Text>
              </View>
            </View>

            {/* Customer + Voucher info */}
            <View style={[styles.infoBlock, styles.sectionDivider]}>
              <View style={styles.infoLeft}>
                <View style={styles.customerFields}>
                  <InfoRow label="Name" value={supplierName} />
                  <InfoRow label="Address" value={supplierAddress} />
                  <InfoRow label="City" value={supplierCity} />
                  <View style={styles.infoRowSplit}>
                    <LabelValue label="Phone No" value={supplierPhone} />
                    <LabelValue label="State" value={supplierState} />
                  </View>
                  <View style={styles.infoRowSplit}>
                    <LabelValue label="GST No." value={supplierGst} />
                    <LabelValue label="PAN No." value={supplierPan} />
                  </View>
                </View>
              </View>

              <View style={styles.infoRight}>
                <View style={styles.voucherBox}>
                  <View style={styles.voucherBoxHeader}>
                    <Text style={styles.voucherBoxHeaderText}>
                      Refinery Issue Voucher
                    </Text>
                  </View>
                  <View style={styles.voucherBoxBody}>
                    <View style={styles.voucherBoxRow}>
                      <Text style={styles.voucherLabel} wrap={false}>
                        Voucher No
                      </Text>
                      <Text style={styles.voucherColon} wrap={false}>
                        :
                      </Text>
                      <Text style={styles.voucherValue} wrap={false}>
                        {docData?.doc_number || ""}
                      </Text>
                    </View>
                    <View style={styles.voucherBoxRow}>
                      <Text style={styles.voucherLabel} wrap={false}>
                        Date
                      </Text>
                      <Text style={styles.voucherColon} wrap={false}>
                        :
                      </Text>
                      <Text style={styles.voucherValue} wrap={false}>
                        {formatDate(docData?.doc_date)}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.voucherMetaRow}>
                  <Text style={styles.voucherLabel} wrap={false}>
                    GST No.
                  </Text>
                  <Text style={styles.voucherColon} wrap={false}>
                    :
                  </Text>
                  <Text style={styles.voucherValue} wrap={false}>
                    {companyGst}
                  </Text>
                </View>
                <View style={styles.voucherMetaRow}>
                  <Text style={styles.voucherLabel} wrap={false}>
                    PAN No.
                  </Text>
                  <Text style={styles.voucherColon} wrap={false}>
                    :
                  </Text>
                  <Text style={styles.voucherValue} wrap={false}>
                    {companyPan}
                  </Text>
                </View>
              </View>
            </View>

            {/* Items table — shares outer box borders, no separate frame */}
            <View style={styles.sectionDivider}>
              <View style={styles.tableHeader}>
                <Text style={[styles.cell, styles.colSr]}>Sr.</Text>
                <Text style={[styles.cell, styles.colDesc]}>Description</Text>
                <Text style={[styles.cell, styles.colHsn]}>HSN</Text>
                <Text style={[styles.cell, styles.colWt]}>Gross Wt</Text>
                <Text style={[styles.cell, styles.colWt]}>Net Wt</Text>
                <Text style={[styles.cell, styles.colTouch]}>Touch</Text>
                <Text style={[styles.cell, styles.colFine]}>Fine Wt</Text>
                <Text style={[styles.cell, styles.colConv]}>Conv. Item</Text>
                <Text style={[styles.cellLast, styles.colTotal]}>
                  Total Wt.
                </Text>
              </View>

              {items.map((item, index) => (
                <View style={styles.tableDataRow} key={item.id || index}>
                  <Text style={[styles.cell, styles.colSr]}>{index + 1}</Text>
                  <Text style={[styles.cell, styles.colDesc]}>
                    {item.item_category_name || item.product_group_name || ""}
                  </Text>
                  <Text style={[styles.cell, styles.colHsn]}>
                    {item.hsn || ""}
                  </Text>
                  <Text style={[styles.cell, styles.colWt]}>
                    {fmtWt(item.gwt)}
                  </Text>
                  <Text style={[styles.cell, styles.colWt]}>
                    {fmtWt(item.nwt)}
                  </Text>
                  <Text style={[styles.cell, styles.colTouch]}>
                    {fmtTouch(item.touch)}
                  </Text>
                  <Text style={[styles.cell, styles.colFine]}>
                    {fmtWt(item.fine_wt)}
                  </Text>
                  <Text style={[styles.cell, styles.colConv]}>
                    {fmtWt(item.conv_item, 2)}
                  </Text>
                  <Text style={[styles.cellLast, styles.colTotal]}>
                    {fmtWt(item.total_wt)}
                  </Text>
                </View>
              ))}

              <View style={styles.tableBlankArea}>
                <View style={[styles.blankCell, styles.colSr]} />
                <View style={[styles.blankCell, styles.colDesc]} />
                <View style={[styles.blankCell, styles.colHsn]} />
                <View style={[styles.blankCell, styles.colWt]} />
                <View style={[styles.blankCell, styles.colWt]} />
                <View style={[styles.blankCell, styles.colTouch]} />
                <View style={[styles.blankCell, styles.colFine]} />
                <View style={[styles.blankCell, styles.colConv]} />
                <View style={[styles.blankCellLast, styles.colTotal]} />
              </View>

              <View style={styles.tableFooterRow}>
                <Text style={[styles.cell, styles.colSr]}></Text>
                <Text style={[styles.cell, styles.colDesc]}></Text>
                <Text style={[styles.cell, styles.colHsn]}></Text>
                <Text style={[styles.cell, styles.colWt]}>
                  {fmtWt(totals.gwt)}
                </Text>
                <Text style={[styles.cell, styles.colWt]}>
                  {fmtWt(totals.nwt)}
                </Text>
                <Text style={[styles.cell, styles.colTouch]}></Text>
                <Text style={[styles.cell, styles.colFine]}>
                  {fmtWt(totals.fineWt)}
                </Text>
                <Text style={[styles.cell, styles.colConv]}></Text>
                <Text style={[styles.cellLast, styles.colTotal]}>
                  {fmtWt(totals.totalWt)}
                </Text>
              </View>
            </View>
          </View>

          {/* Section 2: Remarks */}
          <View style={styles.remarksBox}>
            <Text style={styles.remarksLabel}>Remarks :</Text>
            <Text style={styles.remarksText}>{docData?.remarks || ""}</Text>
          </View>

          {/* Section 3: Signatures */}
          <View style={styles.signatureBox}>
            <View style={styles.signatureTopRow}>
              <Text style={styles.jurisdictionText}>
                SUBJECT TO AHMEDABAD JURISDICTION
              </Text>
              <Text style={styles.forCompanyText}>
                For , {companyName}
              </Text>
            </View>
            <View style={styles.signatureBottomRow}>
              <View style={styles.signatureLeft}>
                <View style={styles.signatureLine} />
                <Text style={styles.signatureCaptionLeft}>
                  (Receiver's Signature)
                </Text>
              </View>
              <View style={styles.signatureRight}>
                <Image
                  src={getTenantBrandImageSrcWithFallback("signature")}
                  style={styles.signatureImage}
                />
                <Text style={styles.signatureCaptionRight}>
                  (Authorised Signatory)
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

function RefineryPdfModal({ show, onHide, refineryId }) {
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!show || !refineryId) return;

      setLoading(true);
      setDocData(null);

      try {
        const response = await showRefinery({ id: refineryId });

        if (!isRefineryApiSuccess(response)) {
          toast.error(response?.message || "Failed to fetch refinery data");
          return;
        }

        const mapped = mapRefineryShowToForm(response);
        if (!mapped) {
          toast.error("Failed to process refinery data");
          return;
        }

        setDocData(mapped);
      } catch (error) {
        console.error("Error fetching refinery voucher data:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch refinery data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [show, refineryId]);

  const pdfContent = useMemo(() => {
    if (!docData) return null;
    return (
      <PDFViewer width="100%" height="100%">
        <RefineryPDFDocument docData={docData} />
      </PDFViewer>
    );
  }, [docData]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="pdf-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Refinery Issue Voucher</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading voucher...</span>
            </div>
          </div>
        ) : pdfContent ? (
          pdfContent
        ) : (
          <div className="text-center p-4">
            <h5 className="text-muted">No Voucher Data Available</h5>
            <p className="text-muted">Unable to load refinery voucher data</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default RefineryPdfModal;
